import {Injectable} from '@angular/core';
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {CampaignWebhookDto} from "../../model/dto/entity/campaign-webhook.dto";
import {CampaignWebhookSubscriptionDto} from "../../model/dto/entity/campaign-webhook-subscription.dto";
import {WebhookDeliveryDto} from "../../model/dto/entity/webhook-delivery.dto";
import {WebhookDeliveryAlertDto} from "../../model/dto/entity/webhook-delivery-alert.dto";
import {
    SubscribeWebhookRequest,
    TestWebhookResponse,
    WebhookDeliveryStats
} from "../../model/dto/api/webhook.interface";

/**
 * Service for CampaignWebhook entity operations.
 * F-033: Webhook and Event Notifications - Provides API access for webhook management.
 */
@Injectable({
    providedIn: 'root'
})
export class CampaignWebhookService extends MvsCrudService {

    constructor(protected http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/ct/campaignWebhooks')
    }

    getObjectIcon(): string {
        return 'fa-solid fa-plug';
    }

    /**
     * Tests a webhook by sending a test payload.
     * F-033: Allows users to verify webhook configuration before activating.
     */
    testWebhook(webhookId: number): Observable<TestWebhookResponse> {
        return this.postGeneric<TestWebhookResponse>({}, `/${webhookId}/test`);
    }

    /**
     * Gets all active webhooks.
     */
    getActiveWebhooks(): Observable<CampaignWebhookDto[]> {
        return this.getGeneric<CampaignWebhookDto[]>('/active');
    }

    /**
     * Gets webhooks owned by a specific user.
     */
    getWebhooksByOwner(ownerId: number): Observable<CampaignWebhookDto[]> {
        return this.getGeneric<CampaignWebhookDto[]>(`/byOwner/${ownerId}`);
    }

    /**
     * Deactivates a webhook.
     */
    deactivateWebhook(webhookId: number): Observable<void> {
        return this.postGeneric<void>({}, `/${webhookId}/deactivate`);
    }

    /**
     * Subscribes a campaign to a webhook.
     * F-033: Acceptance Criteria - "Given webhook is configured, when campaign activates,
     * then webhook is called with campaign data"
     */
    subscribeCampaign(webhookId: number, request: SubscribeWebhookRequest): Observable<CampaignWebhookSubscriptionDto> {
        return this.postGeneric<CampaignWebhookSubscriptionDto>(request, `/${webhookId}/subscribe`);
    }

    /**
     * Unsubscribes a campaign from a webhook.
     */
    unsubscribeCampaign(webhookId: number, campaignId: number): Observable<void> {
        return this.postGeneric<void>({}, `/${webhookId}/unsubscribe/${campaignId}`);
    }

    /**
     * Gets delivery history for a webhook.
     * F-033: Acceptance Criteria - "Given webhook fails, when retry logic runs,
     * then failures are logged and alerted"
     */
    getDeliveryHistory(webhookId: number): Observable<WebhookDeliveryDto[]> {
        return this.getGeneric<WebhookDeliveryDto[]>(`/${webhookId}/deliveries`);
    }

    /**
     * Gets delivery statistics for a webhook.
     */
    getDeliveryStats(webhookId: number): Observable<WebhookDeliveryStats> {
        return this.getGeneric<WebhookDeliveryStats>(`/${webhookId}/stats`);
    }

    /**
     * Gets unacknowledged alerts for a user.
     */
    getUnacknowledgedAlerts(userId: number): Observable<WebhookDeliveryAlertDto[]> {
        return this.getGeneric<WebhookDeliveryAlertDto[]>(`/alerts/${userId}`);
    }

    /**
     * Acknowledges a delivery alert.
     */
    acknowledgeAlert(alertId: number): Observable<void> {
        return this.postGeneric<void>({}, `/alerts/${alertId}/acknowledge`);
    }
}
